import { takeEvery, fork, put, all, call, select } from "redux-saga/effects";
import {
  GET_IMPORT_MODELS_LIST,
  GET_EXPORT_MODELS_LIST,
  EXPORT_STATUS,
  EXPORT_DOWNLOAD_FILE,
  DOWNLOAD_EXPORT_DATA,
  IMPORT_UPLOAD_STATUS,
  UPLOAD_IMPORT_DATA,
  UPLOAD_TABLE_PREVIEW_DATA,
} from "./actionTypes";
import {
  apiError,
  getImportModelsListSuccess,
  getExportModelsListSuccess,
  exportDownloadFile,
  importUploadStatusSuccess,
  uploadImportDataSuccess,
  uploadTablePreviewDataSuccess,
  exportStatus as exportStatusAction,
  importUploadStatus as importUploadStatusAction,
  exportDownloadFileSuccess,
  nextStep,
} from "./actions";
import {
  postAdd,
  getList,
  postDownloadFile,
} from "../../../helpers/Helper";
import { axiosInstance } from "../../../helpers/AxiosHelper";
import Swal from "sweetalert2";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function* getImportModelsListSaga() {
  try {
    const response = yield call(getList, "/reports/generic_import_models/");
    const data = response.data || response;
    if (data && Array.isArray(data)) {
      yield put(getImportModelsListSuccess(data));
    } else {
      throw new Error("Failed to fetch import models.");
    }
  } catch (error) {
    console.error("❌ [Import Models] Error:", error);
    console.error("❌ [Import Models] Error Response:", error.response);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Sorry! There was a server-side issue.",
    });
    yield put(apiError(error.message));
  }
}

function* getExportModelsListSaga() {
  try {
    const response = yield call(getList, "/reports/generic_export_models/");
    const data = response.data || response;
    if (data && Array.isArray(data)) {
      yield put(getExportModelsListSuccess(data));
    } else {
      throw new Error("Failed to fetch export models.");
    }
  } catch (error) {
    console.error("❌ [Export Models] Error:", error);
    console.error("❌ [Export Models] Error Response:", error.response);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data?.detail || "Sorry! There was a server-side issue.",
    });
    yield put(apiError(error.message));
  }
}

function* exportStatusSaga({ payload }) {
  try {
    const response = yield call(
      postAdd,
      `/reports/generic_export/status/${payload.request_id}/`,
      payload,
      {},
    );


    if (response.data.status) {
      Swal.close();
      yield put(
        exportDownloadFile({
          request_id: payload.request_id,
          model_name: payload.model_name,
          file_format: payload.file_format,
        }),
      );
    } else {
      yield call(delay, 10000);
      yield put(exportStatusAction(payload));
    }
  } catch (error) {
    console.error("❌ [Status] Error:", error);
    yield put(apiError(error.message));
  }
}

function* exportDownloadFileSaga({ payload }) {
  try {
    const response = yield call(
      postDownloadFile,
      `/reports/generic_export/download/${payload.request_id}/`,
      payload,
      {},
    );


    if (response.data) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Report downloaded successfully!",
      });
      yield put(exportDownloadFileSuccess(response.data));
    }
  } catch (error) {
    console.error("❌ [Download] Error:", error);
    yield put(apiError(error.message));
  }
}

function* downloadExportDataSaga({ payload }) {
  try {

    Swal.fire({
      text: "Generating Report, please wait...",
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: false,
    });

    const response = yield call(
      postAdd,
      "/reports/generic_export/",
      payload,
      {},
    );


    if (response.status === 201) {
      yield call(delay, 10000);
      yield put(
        exportStatusAction({
          ...payload,
          request_id: response.data.request_id,
        }),
      );
    } else {
      throw new Error("Failed to generate export data.");
    }
  } catch (error) {
    console.error("❌ [Export] Error:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data || error.message,
    });
    yield put(apiError(error.message));
  }
}

function* importUploadStatusSaga({ payload }) {
  try {
   

    let statusCompleted = false;
    let statusResponse;

    while (!statusCompleted) {
      yield call(delay, 2000);

      statusResponse = yield call(
        postAdd,
        `/reports/generic_import/status/${payload.request_id}/`,
        {},
      );


      if (statusResponse.data?.status === true) {
        statusCompleted = true;
      } else {
      }
    }

    Swal.close();
    yield put(importUploadStatusSuccess(statusResponse.data.message));
    yield put(nextStep());
  } catch (error) {
    console.error("❌ [IMPORT_UPLOAD_STATUS] Error:", error);
    console.error("❌ [IMPORT_UPLOAD_STATUS] Error Response:", error.response);
    Swal.close();
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error?.response?.data?.message || error.message || "Import Failed",
    });
    yield put(apiError(error.message));
  }
}

function* uploadImportDataSaga({ payload }) {
  try {
   
    const formData = payload.obj || payload;

    Swal.fire({
      text: "Uploading file, please wait...",
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: false,
    });

    const response = yield call(
      axiosInstance.post,
      "/reports/generic_import/dryrun",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );


    if (response.status === 200) {
      yield put(uploadImportDataSuccess(response.data.request_id));
      yield call(delay, 2000);
      yield put(
        importUploadStatusAction({ request_id: response.data.request_id }),
      );
    } else {
      throw new Error("Failed to upload import data.");
    }
  } catch (error) {


    let errorMessage = "Failed to upload import data.";
    if (error.response?.data) {
      if (typeof error.response.data === "object") {
        errorMessage = JSON.stringify(error.response.data);
      } else {
        errorMessage = error.response.data;
      }
    }

    Swal.fire({
      icon: "error",
      title: "Error",
      text: errorMessage,
    });
    yield put(apiError(error.message));
  }
}

function* uploadTablePreviewDataSaga({ payload }) {
  try {
    const state = yield select();
    const importResponseData = state.importExportReducer.importResponseData;
    const initialData = importResponseData?.initial || {};
    
    
    const dataToSend = {
      model_name: payload.obj.model_name,
      request_id: payload.obj.request_id,
      import_file_name: initialData.import_file_name,
      input_format: initialData.input_format,
      original_file_name: initialData.original_file_name,
    };
    
    
    Swal.fire({
      text: "Processing data, please wait...",
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: false,
    });

    const response = yield call(
      postAdd,
      "/reports/generic_import/process",
      dataToSend,
    );


    if (response.status === 200) {
      Swal.close();
      yield call(delay, 2000);
      yield put(
        importUploadStatusAction({
          request_id: response.data.request_id,
        }),
      );
    } else {
      throw new Error("Failed to process import data.");
    }
  } catch (error) {
    console.error("❌ [UPLOAD_TABLE_PREVIEW_DATA] Error:", error);
    console.error("❌ [UPLOAD_TABLE_PREVIEW_DATA] Error Data:", error.response?.data);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Sorry! There was a server-side issue.",
    });
    yield put(apiError(error.message));
  }
}

export function* watchGetImportModelsList() {
  yield takeEvery(GET_IMPORT_MODELS_LIST, getImportModelsListSaga);
}

export function* watchGetExportModelsList() {
  yield takeEvery(GET_EXPORT_MODELS_LIST, getExportModelsListSaga);
}

export function* watchExportStatus() {
  yield takeEvery(EXPORT_STATUS, exportStatusSaga);
}

export function* watchExportDownloadFile() {
  yield takeEvery(EXPORT_DOWNLOAD_FILE, exportDownloadFileSaga);
}

export function* watchDownloadExportData() {
  yield takeEvery(DOWNLOAD_EXPORT_DATA, downloadExportDataSaga);
}

export function* watchImportUploadStatus() {
  yield takeEvery(IMPORT_UPLOAD_STATUS, importUploadStatusSaga);
}

export function* watchUploadImportData() {
  yield takeEvery(UPLOAD_IMPORT_DATA, uploadImportDataSaga);
}

export function* watchUploadTablePreviewData() {
  yield takeEvery(UPLOAD_TABLE_PREVIEW_DATA, uploadTablePreviewDataSaga);
}

function* importExportSaga() {
  yield all([
    fork(watchGetImportModelsList),
    fork(watchGetExportModelsList),
    fork(watchExportStatus),
    fork(watchExportDownloadFile),
    fork(watchDownloadExportData),
    fork(watchImportUploadStatus),
    fork(watchUploadImportData),
    fork(watchUploadTablePreviewData),
  ]);
}

export default importExportSaga;
